import { useCallback } from 'react'
import { deleteTask, restoreTask, setTaskScope, updateTask, type TaskPatch } from '@/data/tasks'
import { fire } from '@/lib/writeError'
import { doneOnce, moveToDay, moveToTray } from '@/logic/changes'
import { fmtDate, today } from '@/logic/dates'
import { applyCompletion, applyRoutineCompletion, undoCompletionPatch } from '@/logic/recurrence'
import { useCategories } from '@/store/categories'
import { showToast } from '@/store/ui'
import type { Category, DateStr, Scope, Task } from '@/types'
import { usePaths } from './usePaths'

interface Change {
  task: Task
  change: TaskPatch
  before: TaskPatch
}

const tasksWord = (n: number) => `${n} task${n === 1 ? '' : 's'}`

/**
 * The Plan tab's Select actions over several tasks at once, each applying the same rule as the
 * one-task action (ticking a repeat rolls it on, a routine is done for the week, and so on). A batch
 * shares one toast and one Undo. Tasks an action can't apply to are left alone and the toast says so:
 * a private category can't go on a shared task, only shared tasks are assigned, and only the person
 * who made a shared task can make it private.
 */
export function useBulkActions() {
  const paths = usePaths()

  const writeAll = useCallback(
    (changes: Change[], text: string) => {
      for (const c of changes) fire(updateTask(paths, c.task, c.change))
      showToast(text, () => {
        for (const c of changes) fire(updateTask(paths, { ...c.task, ...c.change }, c.before))
      })
    },
    [paths],
  )

  const done = useCallback(
    (tasks: Task[]) => {
      const now = new Date()
      const changes: Change[] = []
      for (const t of tasks) {
        if (t.kind === 'repeat' && t.recurrence) changes.push({ task: t, change: applyCompletion(t, now).patch, before: undoCompletionPatch(t) })
        else if (t.kind === 'routine') changes.push({ task: t, change: applyRoutineCompletion(t, now).patch, before: undoCompletionPatch(t) })
        else if (!t.done) {
          const { patch, before } = doneOnce(t, today(now))
          changes.push({ task: t, change: patch, before })
        }
      }
      writeAll(changes, `${tasksWord(changes.length)} done`)
    },
    [writeAll],
  )

  /** Every task to one day, or with `day` null back to No date (where the task allows it). */
  const moveTo = useCallback(
    (tasks: Task[], day: DateStr | null) => {
      const changes: Change[] = []
      let refused = 0
      for (const t of tasks) {
        const before: TaskPatch = { plannedDate: t.plannedDate, weekOf: t.weekOf, followUpDate: t.followUpDate, followUpTime: t.followUpTime, order: t.order }
        if (day) {
          changes.push({ task: t, change: moveToDay(t, day), before })
          continue
        }
        const result = moveToTray(t, t.kind === 'routine' ? 'routines' : 'nodate', today())
        if ('refused' in result) refused += 1
        else changes.push({ task: t, change: result.patch, before })
      }
      const where = day ? `planned for ${fmtDate(day)}` : 'moved off their days'
      writeAll(changes, `${tasksWord(changes.length)} ${where}${refused ? ` · ${refused} can't move` : ''}`)
    },
    [writeAll],
  )

  const setCategory = useCallback(
    (tasks: Task[], category: Category | null) => {
      // a shared task can't carry a private category: the other person could not read it
      const fits = tasks.filter((t) => !category || category.scope === 'shared' || t.scope === 'private')
      const changes = fits.map((t) => ({ task: t, change: { categoryId: category?.id ?? null }, before: { categoryId: t.categoryId } }))
      const left = tasks.length - fits.length
      writeAll(changes, `${tasksWord(changes.length)} ${category ? `in ${category.name}` : 'without a category'}${left ? ` · ${left} shared left as they were` : ''}`)
    },
    [writeAll],
  )

  /** `assignee` is a member's uid, or null for either of you. Private tasks are only ever their owner's. */
  const assign = useCallback(
    (tasks: Task[], assignee: string | null, label: string) => {
      const shared = tasks.filter((t) => t.scope === 'shared')
      const changes = shared.map((t) => ({ task: t, change: { assigneeUid: assignee }, before: { assigneeUid: t.assigneeUid ?? null } }))
      const left = tasks.length - shared.length
      writeAll(changes, `${tasksWord(changes.length)} for ${label}${left ? ` · ${left} private left as they were` : ''}`)
    },
    [writeAll],
  )

  /** Moves tasks between shared and private. Like the one-task switch, this has no Undo; switch back to undo it. */
  const setScope = useCallback(
    (tasks: Task[], scope: Scope) => {
      const privateIds = new Set(useCategories.getState().private.map((c) => c.id))
      const moving = tasks.filter((t) => t.scope !== scope && (scope === 'shared' || t.createdBy === paths.uid))
      for (const t of moving) fire(setTaskScope(paths, t, scope, privateIds))
      const left = tasks.filter((t) => t.scope !== scope).length - moving.length
      showToast(`${tasksWord(moving.length)} made ${scope}${left ? ` · ${left} not yours to make private` : ''}`)
    },
    [paths],
  )

  const remove = useCallback(
    (tasks: Task[]) => {
      for (const t of tasks) fire(deleteTask(paths, t))
      showToast(`${tasksWord(tasks.length)} deleted`, () => {
        for (const t of tasks) fire(restoreTask(paths, t))
      })
    },
    [paths],
  )

  return { done, moveTo, setCategory, assign, setScope, remove }
}
